import { TextPart, TrackCodec } from "./types";

/*---------------------------------------------------------------------------
  Bhatkhande sargam encoding — 4 characters per swara:

        position:  0   1   2   3
                   B   H   T   U
    0 (B) base, always "1"
    1 (H) kan swara (grace note) flag: 1 = yes, 0 = no
    2 (T) saptak (octave): 1 = madhya, 2 = mandra, 3 = taar
    3 (U) the swar itself, hex

  A cell holds several of these concatenated, one per note in that beat.

  This module is pure: it moved here out of the UI layer, where the decoder
  used to return JSX and therefore could not run on a server.
---------------------------------------------------------------------------*/

export type IAcceptableKeyInputs =
    | "s"
    | "r"
    | "R"
    | "g"
    | "G"
    | "m"
    | "M"
    | "p"
    | "d"
    | "D"
    | "n"
    | "N"
    | " ";

export type IToggleableKeyInputs = Exclude<IAcceptableKeyInputs, " ">;

export interface SwaraSignal {
    key: IAcceptableKeyInputs;
    mandra: boolean;
    taar: boolean;
    kanSwar: boolean;
}

/** Komal/tivra partners. Sa and Pa have no alternate. */
export const alternateKeyMappings: Record<
    Exclude<IToggleableKeyInputs, "s" | "p">,
    Exclude<IToggleableKeyInputs, "s" | "p">
> = {
    r: "R",
    R: "r",
    g: "G",
    G: "g",
    m: "M",
    M: "m",
    d: "D",
    D: "d",
    n: "N",
    N: "n",
};

export const acceptableKeyInputs: IAcceptableKeyInputs[] = [
    "s",
    "r",
    "R",
    "g",
    "G",
    "m",
    "M",
    "p",
    "d",
    "D",
    "n",
    "N",
    " ",
];

export const getAcceptableKeyInputs = (
    str: string
): IAcceptableKeyInputs | null =>
    acceptableKeyInputs.indexOf(str as IAcceptableKeyInputs) !== -1
        ? (str as IAcceptableKeyInputs)
        : null;

/** QWERTY key -> swar code. */
const KEY_TO_CODE: Record<string, string> = {
    s: "1",
    r: "2",
    R: "3",
    g: "4",
    G: "5",
    m: "6",
    M: "7",
    p: "8",
    d: "9",
    D: "A",
    n: "B",
    N: "C",
    " ": "X",
};

/** Swar code -> Devanagari. "X" is a rest. */
const CODE_TO_SWARA: Record<string, string> = {
    "1": "सा",
    "2": "रे्",
    "3": "रे",
    "4": "ग्",
    "5": "ग",
    "6": "म",
    "7": "मे",
    "8": "प",
    "9": "ध्",
    A: "ध",
    B: "नि्",
    C: "नि",
    X: "S",
};

/** Saptak code -> the mark appended to the swara. */
const SAPTAK_MARK: Record<string, string> = {
    "1": "", // madhya
    "2": "़", // mandra (dot below)
    "3": "ं", // taar (dot above)
};

const REST_CODE = "X";
export const ENCODED_SWARA_LENGTH = 4;

/** Emitted when a value is not a whole number of swaras. */
export const MALFORMED_SENTINEL = "!!";

export function encodeSwara(signal: SwaraSignal): string {
    const swar = KEY_TO_CODE[signal.key];
    // taar takes precedence when both octave modifiers are somehow active
    const saptak = signal.taar ? 3 : signal.mandra ? 2 : 1;
    const kan = signal.kanSwar ? 1 : 0;
    return `1${kan}${saptak}${swar}`;
}

function splitIntoSwaras(value: string): string[] {
    const chunks: string[] = [];
    for (let i = 0; i < value.length; i += ENCODED_SWARA_LENGTH) {
        chunks.push(value.slice(i, i + ENCODED_SWARA_LENGTH));
    }
    return chunks;
}

const isWellFormed = (value: string): boolean =>
    value.length % ENCODED_SWARA_LENGTH === 0;

/** Stored value -> display parts. Never returns JSX. */
export function decodeToParts(value: string): TextPart[] {
    if (!isWellFormed(value)) {
        return [{ text: MALFORMED_SENTINEL }];
    }
    return splitIntoSwaras(value).map((chunk) => {
        const isKanSwar = chunk[1] === "1";
        const text =
            (CODE_TO_SWARA[chunk[3]] ?? "") + (SAPTAK_MARK[chunk[2]] ?? "");
        return isKanSwar ? { text, superscript: true } : { text };
    });
}

/**
 * Stored value -> plain text.
 *
 * Kan swaras and rests are omitted: a summary reads the melodic line, not its
 * ornaments. This matches the original `decodeToString` behaviour exactly.
 */
export function decodeToPlainText(value: string): string {
    if (!isWellFormed(value)) {
        return MALFORMED_SENTINEL;
    }
    return splitIntoSwaras(value)
        .filter((chunk) => chunk[1] !== "1" && chunk[3] !== REST_CODE)
        .map(
            (chunk) =>
                (CODE_TO_SWARA[chunk[3]] ?? "") + (SAPTAK_MARK[chunk[2]] ?? "")
        )
        .join(" ");
}

/** Key -> Devanagari label, for keyboard buttons. */
export function swaraKeyLabels(): Record<string, string> {
    const labels: Record<string, string> = {};
    Object.keys(KEY_TO_CODE).forEach((key) => {
        labels[key] = CODE_TO_SWARA[KEY_TO_CODE[key]];
    });
    return labels;
}

/** Notes that occupy time — kan swaras are ornaments and do not count. */
export function countSoundedNotes(value: string): number {
    if (!isWellFormed(value)) return 0;
    return splitIntoSwaras(value).filter((chunk) => chunk[1] === "0").length;
}

export const sargamCodec: TrackCodec = {
    id: "qwerty-sargam",
    label: "Sargam (Bhatkhande)",
    isFreeText: false,
    toParts: decodeToParts,
    toPlainText: decodeToPlainText,
    // Four stored characters render as roughly one glyph, hence the halving.
    // Preserved verbatim from the original inline layout arithmetic.
    measure: (value) => (value.length * 10 + 10) / 2 + 0.1,
    countNotes: countSoundedNotes,
    // Subcells concatenate: each is a whole number of 4-char swaras, so the
    // beat decodes as one sequence.
    summariseCell: (subCells) => decodeToPlainText(subCells.join("")),
};
